/**
 * Custom Tables Co — snowfall overlay.
 *
 * Canvas rather than DOM nodes: a few hundred animated elements each with their
 * own transform will jank a product page on a mid-range phone, and this sits
 * over the whole site including checkout-adjacent pages. One canvas, one rAF.
 *
 * It backs off wherever it would cost the shopper something:
 *   - prefers-reduced-motion  → does not run at all
 *   - hidden tab             → pauses the loop
 *   - coarse pointer / small viewport → fewer flakes
 * The canvas is pointer-events:none and aria-hidden, so it can never intercept
 * a tap on a Buy button.
 */

const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

class MJSnow extends HTMLElement {
  connectedCallback() {
    if (REDUCED.matches) return;

    this.canvas = document.createElement('canvas');
    this.canvas.setAttribute('aria-hidden', 'true');
    this.ctx = this.canvas.getContext('2d', { alpha: true });
    this.appendChild(this.canvas);

    this.density = Number(this.dataset.density) || 60;
    this.speed = Number(this.dataset.speed) || 1;
    this.maxRadius = Number(this.dataset.size) || 3;
    this.color = this.dataset.color || '#ffffff';

    this.onResize = () => this.#resize();
    this.onVisibility = () => this.#toggleLoop();

    window.addEventListener('resize', this.onResize, { passive: true });
    document.addEventListener('visibilitychange', this.onVisibility);

    this.#resize();
    this.#toggleLoop();
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.raf);
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  #resize() {
    // Cap DPR at 2 — beyond that the extra pixels cost more than they show.
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.w = window.innerWidth;
    this.h = window.innerHeight;

    this.canvas.width = this.w * dpr;
    this.canvas.height = this.h * dpr;
    this.canvas.style.width = `${this.w}px`;
    this.canvas.style.height = `${this.h}px`;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // Scale flake count to viewport area so a phone is not doing desktop work.
    const areaFactor = Math.min(1, (this.w * this.h) / (1440 * 900));
    const count = Math.round(this.density * areaFactor);
    this.flakes = Array.from({ length: count }, () => this.#spawn(true));
  }

  #spawn(anywhere) {
    return {
      x: Math.random() * this.w,
      y: anywhere ? Math.random() * this.h : -10,
      r: Math.random() * this.maxRadius + 0.6,
      // Bigger flakes read as nearer, so they fall faster and drift less.
      vy: (Math.random() * 0.6 + 0.35) * this.speed,
      drift: (Math.random() - 0.5) * 0.6,
      phase: Math.random() * Math.PI * 2,
      alpha: Math.random() * 0.5 + 0.35,
    };
  }

  #toggleLoop() {
    cancelAnimationFrame(this.raf);
    if (document.hidden) return;
    this.last = performance.now();
    this.raf = requestAnimationFrame((t) => this.#frame(t));
  }

  #frame(now) {
    // Normalise to 60fps so the fall rate is the same on a 120Hz display.
    const dt = Math.min((now - this.last) / 16.67, 3);
    this.last = now;

    const { ctx } = this;
    ctx.clearRect(0, 0, this.w, this.h);
    ctx.fillStyle = this.color;

    for (const f of this.flakes) {
      f.y += f.vy * f.r * dt;
      f.phase += 0.01 * dt;
      f.x += Math.sin(f.phase) * f.drift * dt;

      if (f.y - f.r > this.h) Object.assign(f, this.#spawn(false));
      if (f.x < -10) f.x = this.w + 10;
      if (f.x > this.w + 10) f.x = -10;

      ctx.globalAlpha = f.alpha;
      ctx.beginPath();
      ctx.arc(f.x, f.y, f.r, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.globalAlpha = 1;
    this.raf = requestAnimationFrame((t) => this.#frame(t));
  }
}

if (!customElements.get('ct-snow')) {
  customElements.define('ct-snow', MJSnow);
}
